import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import Context from 'managers/Context';
import { FamilyRatesModel } from 'models';
import { getLiteral } from 'utils/getLiteral';

import { RatesAddItemsEmpty } from '../shared/RatesAddItemsFeedback';
import RatesAddItemRow from '../shared/RatesAddItemRow';
import RatesAddItemsModal from '../shared/RatesAddItemsModal';

const defaultGetSelectedItemId = (item) => item?.id ?? item?.entity?.id;

const FamilyRatesAddModal = memo(
    ({
        familiesList,
        getSelectedItemId = defaultGetSelectedItemId,
        handleClose,
        onAddLine,
        open,
        title,
    }) => {
        const [search, setSearch] = useState('');
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(false);
        const [result, setResult] = useState([]);

        const loadFamilies = useCallback((text) => {
            setLoading(true);
            setError(false);
            Context.valueListManager.getValueList(
                'tblclevel2',
                (items) => {
                    const normalized = text.trim().toLowerCase();
                    const filtered = normalized
                        ? items.filter((item) =>
                              item.descripcion?.toLowerCase().includes(normalized),
                          )
                        : items;
                    setResult(filtered);
                    setLoading(false);
                    setError(false);
                },
                () => {
                    setResult([]);
                    setError(true);
                    setLoading(false);
                },
            );
        }, []);

        useEffect(() => {
            if (open) {
                setSearch('');
                loadFamilies('');
            }
        }, [loadFamilies, open]);

        const selectedIds = useMemo(() => {
            const ids = new Set();
            familiesList?.forEach((item) => {
                const itemId = getSelectedItemId(item);
                if (itemId != null && itemId !== '') {
                    ids.add(String(itemId));
                }
            });
            return ids;
        }, [familiesList, getSelectedItemId]);

        const handleAdd = useCallback(
            (element) => {
                onAddLine?.(FamilyRatesModel.fromFuzzySearch(element));
            },
            [onAddLine],
        );

        const handleSearch = useCallback(
            (text) => {
                setSearch(text);
                loadFamilies(text);
            },
            [loadFamilies],
        );

        const renderContent = () => {
            if (result.length === 0) {
                return (
                    <div className="rates-add-items-modal__empty">
                        <RatesAddItemsEmpty />
                    </div>
                );
            }

            return (
                <div className="rates-add-items-modal__list">
                    {result.map((element, index) => {
                        const id = Number(element['-id']);
                        return (
                            <RatesAddItemRow
                                key={element['-id'] ?? index}
                                isAdded={selectedIds.has(String(id))}
                                label={element.descripcion}
                                onAction={() => handleAdd(element)}
                            />
                        );
                    })}
                </div>
            );
        };

        return (
            <RatesAddItemsModal
                error={error}
                isOpen={open}
                loading={loading}
                onClose={handleClose}
                onSearch={handleSearch}
                onSearchInputChange={setSearch}
                placeholder={getLiteral('action_search_families')}
                searchValue={search}
                title={title}
            >
                {renderContent()}
            </RatesAddItemsModal>
        );
    },
);

FamilyRatesAddModal.displayName = 'FamilyRatesAddModal';

export default FamilyRatesAddModal;
